////////////////////////////////////////////
// PROTOCOL CONSTANTS — HAND-MIRRORED FROM THE ON-CHAIN SOURCES
////////////////////////////////////////////
//
// SAME CONVENTION AS THE VANILLA APP'S config.js: THESE VALUES MIRROR
// programs/fuze/src/constants.rs (SOLANA) AND contracts/src/FuzeRH.sol /
// FuzeInstantV2.sol (ROBINHOOD CHAIN). THEY ARE ENFORCED ON-CHAIN; THE UI
// COPIES EXIST ONLY FOR CLIENT-SIDE VALIDATION AND DISPLAY. A PROGRAM OR
// CONTRACT REDEPLOY MEANS UPDATING THIS FILE AND SHIPPING A NEW BUILD.

// CORE UNITS
export const SOL = 1e9;
export const WEI = 1e18;
export const DEFAULT_PUBKEY = "11111111111111111111111111111111";

// LAUNCH STATUS DISCRIMINANTS (programs/fuze/src/state.rs::LaunchStatus) —
// THE CANONICAL ENUM EVERY RENDER SURFACE READS
export const ST = { ACTIVE: 0, SETTLED: 1, FAILED: 2, CANCELLED: 3, FILLED: 4 } as const;

// RH LAUNCH STATUS DISCRIMINANTS (contracts/src/FuzeRH.sol::LaunchStatus) —
// A DIFFERENT ENUM (RH 1 = FILLED, SOLANA 1 = SETTLED); ONLY THE RH DETAIL
// FLOWS READ IT RAW, EVERYTHING ELSE GOES THROUGH RH_STATUS_MAP
export const RH_ST = { ACTIVE: 0, FILLED: 1, FAILED: 2, IGNITED: 3, ABORTED: 4 } as const;

// RH STATUS -> CANONICAL ST (IGNITED READS AS SETTLED)
export const RH_STATUS_MAP: Record<number, number> = {
  [RH_ST.ACTIVE]: ST.ACTIVE,
  [RH_ST.FILLED]: ST.FILLED,
  [RH_ST.IGNITED]: ST.SETTLED,
  [RH_ST.FAILED]: ST.FAILED,
  [RH_ST.ABORTED]: ST.CANCELLED,
};

////////////////////////////////////////////
// SOLANA — NET-AWARE FLOORS + CAPS (PRODUCTION VALUES — BETA'S 1 SOL/33%
// REVERTED 2026-07-24; MIRROR program constants.rs + shared/constants.mjs)
////////////////////////////////////////////
export function solanaLimits(isDevnet: boolean) {

  return {
    GOAL_FLOOR: isDevnet ? 0.02 * SOL : 10 * SOL,
    DEFAULT_GOAL: isDevnet ? 0.05 * SOL : 42 * SOL,
    MAX_WALLET_BPS: 1000,
    DEFAULT_DURATION_SECS: isDevnet ? 600 : 86_400,
  };
}

// FIXED SOLANA CONSTANTS
export const LAUNCH_FEE = 42_000_000;
export const RENT_RESERVE = 60_000_000;
export const FEE_BPS = 100;
export const SHARE_CAP_BPS = 690;
export const MIN_WALLET_BPS = 100;
// CREATOR VESTING = LOCK (CLIFF) + DRIP (POST-CLIFF LINEAR SPAN)
export const MIN_LOCK_SECS = 0;
export const MAX_LOCK_SECS = 3600;
export const MIN_DRIP_SECS = 60;
export const MAX_DRIP_SECS = 3600;
export const MIN_COMMIT = 10_000_000;
export const MIN_COMMIT_FLOOR = 1_000_000;
export const DEFAULT_CAP_BPS = 1000;
export const MAX_NAME = 32;
export const MAX_SYM = 10;
export const MAX_URI = 200;

////////////////////////////////////////////
// SOLANA INSTANT (pump.fun venue, U-I5 — docs/pump-instant-build-plan.md) —
// MAINNET ONLY, pump.fun has no devnet program to create against.
////////////////////////////////////////////
// Mirrors instant-create.service.mjs's FLAT_CREATE_FEE_LAMPORTS (U-I0 decision).
export const SOL_INSTANT_LAUNCH_FEE = 20_000_000; // 0.02 SOL
// Mirrors pump-curve.mjs's INSTANT_DEV_CAP_PCT — the server (assertDevBuyWithinCap)
// is the real enforcement; this is display-only for the client-side estimate below.
export const SOL_INSTANT_DEV_CAP_PCT = 5;
// pump's protocol-wide virgin-curve constants — verified live against a real
// mainnet Global account fetch during U-I2's build (docs/pump-instant-build-plan.md).
// A fresh pump coin ALWAYS starts with these exact reserves; no creator-supplied
// liquidity exists on this venue (unlike RH instant) — the bonding curve IS the
// market from the instant the coin is created.
export const PUMP_INITIAL_VIRTUAL_SOL_LAMPORTS = 30 * SOL; // 30 SOL
export const PUMP_INITIAL_VIRTUAL_TOKEN_RESERVES = 1_073_000_000_000_000; // raw units, 6dp
export const PUMP_TOKEN_TOTAL_SUPPLY = 1_000_000_000_000_000; // raw units, 6dp

////////////////////////////////////////////
// ROBINHOOD CHAIN — CHAIN IDS, DEPLOYED ADDRESSES, PROTOCOL CONSTANTS
////////////////////////////////////////////
//
// CONTRACTS ARE IMMUTABLE — A NEW VERSION IS A NEW FACTORY DEPLOY, SO THESE
// ADDRESSES ARE PINNED PER NET AND UPDATED BY HAND ON REDEPLOY.
export const RH_CHAIN_ID_MAINNET = 4663;
export const RH_CHAIN_ID_TESTNET = 46630;

export const RH_EXPLORER_MAINNET = "https://robinhoodchain.blockscout.com";
export const RH_EXPLORER_TESTNET = "https://explorer.testnet.chain.robinhood.com";

// KEYLESS PUBLIC RPC — SAFE CLIENT-SIDE (QUOTES MUST WORK WITH NO WALLET)
export const RH_PUBLIC_RPC_MAINNET = "https://rpc.mainnet.chain.robinhood.com";
export const RH_PUBLIC_RPC_TESTNET = "https://rpc.testnet.chain.robinhood.com";

// FUZE FACTORY (FAIR LAUNCH) — v1 MAINNET LEDGER-DEPLOYED 2026-07-14. v1
// launches resolve forever on whichever factory they were created on (routed
// by the launch key's own `0xfactory:id` prefix, never by a "current address"
// constant) — see RH_FAIR_FACTORIES_* below for the per-net allowlist that
// covers both versions.
export const RH_FUZE_ADDRESS_V1_MAINNET = "0x2d482bfA8607078FD60344A99cE66C5bfa70753d";
export const RH_FUZE_ADDRESS_V1_TESTNET = "0x662150FF7616fA43736E68C724259dE450C02569";

// FUZE FACTORY V2 (0.4 ETH goal floor / 10% wallet cap immutable constants —
// docs/rh-v2-params-plan.md). Testnet rehearsal deployed 2026-07-24 (U-2).
// U-5 FLIPS THIS: mainnet v2 doesn't exist yet (Ledger ceremony pending) — set
// RH_FAIR_V2_ADDRESS_MAINNET once it's deployed and CREATE moves to v2 on
// mainnet too, automatically, via the fallback below. Nothing else changes.
export const RH_FAIR_V2_ADDRESS_TESTNET = "0xAFffBa8952c93AF115a8Dd31005734DAe7FBa56B";
export const RH_FAIR_V2_ADDRESS_MAINNET = "0xf9c7720d8952d8a05A4b5619Ebd202827bb52BCF"; // U-5: deployed 2026-07-25, block 19275580 (0.4 ETH / 10% immutable)

// CREATE TARGET — v2 where it exists, else v1. This is the ONLY thing that
// should ever consume "the current fair factory" as a single address (the
// create submit flow); everything else (commit writes, launch-page display,
// audit/attest) routes by the launch key's own factory and must use
// RH_FAIR_FACTORIES_* / the key itself, never these two.
export const RH_FUZE_ADDRESS_MAINNET = RH_FAIR_V2_ADDRESS_MAINNET || RH_FUZE_ADDRESS_V1_MAINNET;
export const RH_FUZE_ADDRESS_TESTNET = RH_FAIR_V2_ADDRESS_TESTNET || RH_FUZE_ADDRESS_V1_TESTNET;

// ALL FAIR FACTORIES EVER DEPLOYED, PER NET — the allowlist a post-creation
// write (commit) checks a launch key's factory against, so a legitimate v1
// OR v2 launch can still be committed to after CREATE has moved on to v2.
export const RH_FAIR_FACTORIES_MAINNET = [RH_FUZE_ADDRESS_V1_MAINNET, RH_FAIR_V2_ADDRESS_MAINNET].filter(Boolean);
export const RH_FAIR_FACTORIES_TESTNET = [RH_FUZE_ADDRESS_V1_TESTNET, RH_FAIR_V2_ADDRESS_TESTNET].filter(Boolean);

// FUZE INSTANT (EXPRESS LANE) — V2 IS WHERE NEW LAUNCHES GO; V1 RESOLVES FOREVER
export const RH_INSTANT_ADDRESS_MAINNET = "0x17F950C16499d200c8cb896E23b68eD683Dc7202";
export const RH_INSTANT_ADDRESS_TESTNET = "0xfcdd4b7D5e99cD62743686B683205fcdEA56b952";
export const RH_INSTANT_V2_ADDRESS_MAINNET = "0xCff8b7b9eb345ac6715342a05ea9A49e66185A61";
export const RH_INSTANT_V2_ADDRESS_TESTNET = "0x4Bfd7B66c0584D3A846242EE5E7c8c9fA8b1298f";

// FEE LOCKER V2 — REJECTED AS A FEE RECIPIENT (WOULD STRAND CREATOR FEES)
export const RH_FEE_LOCKER_V2_ADDRESS_MAINNET = "0x854278a6fAc0E9DBe8a30F174623f7accaCa8095";
export const RH_FEE_LOCKER_V2_ADDRESS_TESTNET = "0x11fa34C127009977221F69f304e723281780f4eA";

// SWAP (UNISWAP V3) — ROUTER VARIANT DIFFERS BY NET: MAINNET IS SwapRouter02
// (NO deadline IN ExactInputSingleParams), TESTNET IS THE ORIGINAL SwapRouter
// (deadline REQUIRED). CALLERS BRANCH ON routerIs02.
export const RH_WETH_MAINNET = "0x0Bd7D308f8E1639FAb988df18A8011f41EAcAD73";
export const RH_WETH_TESTNET = "0x9BB8E498f90C63FaCb6855a157229B1Bd2685907";
export const RH_SWAP_ROUTER_MAINNET = "0xCaf681a66D020601342297493863E78C959E5cb2";
export const RH_SWAP_ROUTER_TESTNET = "0xeaA107265cD70101168BAfB3A33c7dcaB7553915";
export const RH_QUOTER_V2_MAINNET = "0x33e885eD0Ec9bF04EcfB19341582aADCb4c8A9E7";
export const RH_QUOTER_V2_TESTNET = "0xDcd1C2c44405E2E183baB6588F93aE1B0eb1fF4D";
export const RH_POOL_FEE_TIER = 3000;

// WALLETCONNECT (REOWN) PROJECT ID — cloud.reown.com "Fuze" PROJECT
export const WC_PROJECT_ID = "1465af5d2cc7c53edd338dbd3e4948a2";

// RH PROTOCOL CONSTANTS — ETH-DENOMINATED (CONVERT AT POINT OF USE VIA
// viem parseEther; NEVER DO NUMBER MATH ON WEI-SCALE INTEGERS)
// PRODUCT floor/cap (2026-07-24, Sam: dollar-parity with Solana's 10 SOL
// floor — 0.4 ETH ≈ $744 vs $739 at set time — and the same 10% cap). The
// DEPLOYED FuzeRH contract's own bounds are LOOSER (GOAL_FLOOR 0.01 ETH,
// MAX_WALLET_BPS 3300, both immutable constants) — these stricter values are
// enforced here at the create UI, the only launch builder; a direct contract
// call can still create looser launches until a v2 contract pins them.
export const RH_GOAL_FLOOR_ETH = 0.4;
export const RH_LAUNCH_FEE_ETH = 0.002;
export const RH_MIN_COMMIT_ETH = 0.001;
export const RH_MIN_COMMIT_FLOOR_ETH = 0.0002;
export const RH_MIN_WALLET_BPS = 100;
export const RH_MAX_WALLET_BPS = 1_000;
export const RH_COMPLETION_FEE_BPS = 190;
export const RH_REFUND_FEE_BPS = 100;
export const RH_CREATOR_BPS = 690;
// BUYER-SIDE POOL AS BPS OF SUPPLY (30%) — MIRRORS FuzeRH.sol's BUYER_BPS.
// The creator's 6.9% is carved from THIS pool, so the creator's slice of the
// full supply is RH_BUYER_BPS/1e4 * RH_CREATOR_BPS/1e4 = 2.07%.
export const RH_BUYER_BPS = 3_000;
export const RH_CREATOR_UNLOCK_MIN_SECS = 60;
export const RH_CREATOR_UNLOCK_MAX_SECS = 600;
export const RH_MAX_NAME = 32;
export const RH_MAX_SYM = 10;
export const RH_MAX_URI = 200;

// INSTANT LAUNCH ECONOMICS
export const RH_INSTANT_CREATOR_FEE_PCT = 70;
export const RH_INSTANT_LAUNCH_FEE_ETH = 0.002;
export const RH_INSTANT_MIN_LIQUIDITY_ETH = 0.01;
// BIGINT — 1e27 EXCEEDS Number.MAX_SAFE_INTEGER
export const RH_INSTANT_SUPPLY = 1_000_000_000_000_000_000_000_000_000n;

////////////////////////////////////////////
// AUCTION-DERIVED CURVE (V3, U-F0 — docs/auction-curve-frontend-plan.md /
// docs/auction-curve-design.md) — MIRRORS contracts/src/FuzeCurveMath.sol's
// LOCKED tranche set (Sam-locked 2026-07-26). T/s_a/s_c/m ARE THE DESIGN
// DOC'S NAMES; ρ (RHO) AND THE GRAD MULTIPLE ARE DERIVED, NOT INDEPENDENTLY
// TUNABLE — auction-math.ts (U-F2) DERIVES P₀/R/FDV FROM THESE + auctionRaise,
// AND MUST MATCH FuzeCurveMath.sol'S T_WHOLE/LOCKED_SA_BPS/LOCKED_SC_BPS/
// LOCKED_M EXACTLY (THAT UNIT'S GREEN-LIGHT ASSERTS IT).
export const V3_TOTAL_SUPPLY = 1_000_000_000; // T — WHOLE TOKENS, NOT BASE UNITS (FuzeCurveMath.sol's T_WHOLE)
export const V3_S_A = 0.30; // FuzeCurveMath.sol LOCKED_SA_BPS (3_000 / 1e4)
export const V3_S_C = 0.45; // FuzeCurveMath.sol LOCKED_SC_BPS (4_500 / 1e4)
export const V3_M = 4; // FuzeCurveMath.sol LOCKED_M
export const V3_RHO = (V3_M * V3_S_A) / V3_S_C; // = 8/3 — NOT independently set; ρ = m·s_a/s_c
export const V3_GRAD_MULT = V3_RHO ** 2; // launch→grad FDV multiple, ≈7.11

// INSTANT-CURVE (V3, U-I6 — docs/instant-curve-design.md "The numbers,
// locked") — REUSES the SAME tranche shape above (s_a/s_c/m kept identical
// to the auction lane; see that doc's own non-goal: "no re-tuning the
// tranche shape"). Two NEW locked numbers with no auction analog: R (the
// marketed graduation bar — the number a buyer/creator actually sees) and
// the minimum creator dev-buy (a spam floor, NOT the anti-self-funding
// deterrent — R alone carries that, per the design doc's own priced
// self-funding-attack analysis). Both also read live from the deployed
// FuzeFactoryV3 (MIN_DEV_BUY_WEI()/INSTANT_A_REF_WEI()) wherever a
// wallet-write path needs the authoritative on-chain value — these
// hand-mirrored constants are for instant, no-RPC-round-trip DISPLAY only,
// same "the contract call is source of truth, the constant just paints
// faster" convention V3_S_A/V3_S_C/V3_M above already establish.
export const INSTANT_V3_R_ETH = 3.45; // graduation bar — FuzeFactoryV3.sol's design doc, locked
export const INSTANT_V3_MIN_DEV_BUY_ETH = 0.069; // FuzeFactoryV3.MIN_DEV_BUY_WEI(), locked

// FuzeFactoryV3 — THE AUCTION-DERIVED-CURVE LAUNCH FACTORY (contracts/src/
// FuzeFactoryV3.sol). NOTE: NOT THE SAME CONTRACT AS shared/rh-constants.mjs's
// RH_V3_FACTORY_MAINNET/TESTNET (those are the Uniswap-v3 SWAP factory — an
// unrelated contract that happens to share the "v3" name). Naming here
// follows the server's own shared/rh-web3.mjs::rhAuctionV3Address /
// RH_(TESTNET_)AUCTION_V3_ADDRESS env vars to avoid that collision. Server-
// side this is env-sourced (a redeploy gets a new address, same pattern as
// RH_FAIR_V2_ADDRESS_*); the frontend hand-mirrors the current deployment
// like every other RH factory address in this file — update by hand on
// redeploy, never derive from env at build time.
export const RH_AUCTION_V3_ADDRESS_TESTNET = "0x23d2Ac33448eeb4FD9c743a7587883571c35Cf31"; // U-A9 testnet deploy, 2026-07-27
// U-A12 mainnet ceremony, 2026-07-30 (Ledger-signed, block 24175010). The
// other three addresses from that ceremony are NOT needed here — the frontend
// only ever talks to the factory — but they are recorded in
// contracts/script/DeployAuctionCurveMainnet.s.sol's history and in THREAT.md
// V3.11's re-verify list: FuzeInitGate 0x7df2842887E231A7F8414f6Bf9412797C9B92000,
// FeeLockerV3 0x3440E7141ac145F43Ba0B0B2279C559734115e73, FuzeCurveV3Deployer
// 0xac499e78419B8933105Cd04A2B9541453F94dFa2.
// FACTORY #2 — the CURRENT factory, Ledger-deployed 2026-08-05 at block
// 28610900 with **aMin 0.69 ETH** (factory #1 below shipped with 2 ETH, an
// immutable, which is the entire reason a second factory exists). All NEW
// launches go here. Its siblings: FuzeInitGate
// 0x44d58f3199a055c741236d0F83Be1ee153996000, FeeLockerV3
// 0x9401a21c29511E23bF9d320D1ebce4F41125F799, FuzeCurveV3Deployer
// 0x556b7Cbe3c868eDB191784343f2814F9ac186548.
//
// FACTORY #1 (0xd3F320501D0b234b9158274Ce4dE9354CE310E51, aMin 2 ETH, block
// 24175010) is NOT referenced here and does not need to be: it stays live
// on-chain serving $TND, but the frontend only ever needs the factory it
// CREATES against. Existing launches are read by their own curve/auction
// address via /api/launch, which carries each row's factory from the DB.
export const RH_AUCTION_V3_ADDRESS_MAINNET = "0x9936380DD9B3BA49F5d89e0Ad3c49E2aAa0fea35";

// POOL-LINK BUILDER NEEDS THESE CLIENT-SIDE (FuzeCurveV3.CANONICAL_FEE /
// CANONICAL_TICK_SPACING are on-chain but immutable across every V3 curve —
// safe to hand-mirror, same as RH_POOL_FEE_TIER above for Uniswap v3).
export const V3_POOL_FEE_TIER = 10_000; // FuzeCurveV3.sol CANONICAL_FEE (1%)
export const V3_POOL_TICK_SPACING = 200; // FuzeCurveV3.sol CANONICAL_TICK_SPACING

// U-F5's "locked-pool proof (position token id -> explorer link)": a v4
// position isn't identified by a contract address the way a v3 pool is (v4
// pools live in one singleton PoolManager, keyed by poolId) — the ONLY
// linkable proof is the position NFT itself, minted by the official v4
// PERIPHERY PositionManager (contracts/script/RhV4Addresses.sol). Blockscout's
// standard NFT-instance route is /token/<contract>/instance/<id>. SAME VALUE
// ON BOTH RH NETS (RhV4Addresses.sol's TESTNET_POSITION_MANAGER is literally
// set to = MAINNET_POSITION_MANAGER) — one constant, not a per-net pair, same
// pattern as V3_POOL_FEE_TIER/V3_POOL_TICK_SPACING above.
export const RH_V4_POSITION_MANAGER = "0x58daec3116aae6D93017bAAea7749052E8a04fA7";

// COMMIT WINDOW BOUNDS (U-F3) — FuzeAuction.sol's IMMUTABLE constants
// (MIN_COMMIT_WINDOW = 10 minutes, MAX_COMMIT_WINDOW = 7 days), verified
// against the real contract, not the design doc's "creator-set duration
// within protocol bounds" prose. Safe to hand-mirror like every other
// protocol constant in this file — these never change per-deployment
// (unlike aMin, which is a FACTORY constructor param, read live instead).
/// Factory #2's `aMin` — the protocol-wide MINIMUM RAISE, mirrored here for
/// static projections only. An auction that closes below this is SCRUBBED and
/// every committer refunded, so it is also the floor of any meaningful
/// fee/graduation projection.
///
/// MIRRORS AN IMMUTABLE: the real value lives on the factory
/// (RH_AUCTION_V3_ADDRESS_MAINNET's `aMin()`), and anything transactional MUST
/// read it from chain rather than trust this — submit.ts does exactly that
/// before letting a launch through. This constant exists because
/// AuctionFeeCalc's slider is a pure projection with no wallet/RPC in scope.
/// If a future factory changes aMin, change it here too — a stale value here
/// mis-quotes the deal to creators (it did: this was 2 ETH, factory #1's floor,
/// and the panel advertised an 8 ETH graduation bar after factory #2 moved the
/// real one to 2.76).
export const V3_A_MIN_ETH = 0.69;

/// `R = m·A` with the locked tranche shape's m=4 — the graduation bar scales
/// with the MEASURED raise, so at the floor it is 2.76 ETH.
export const V3_GRAD_MULTIPLE = 4;

export const V3_MIN_COMMIT_WINDOW_SECS = 600;
export const V3_MAX_COMMIT_WINDOW_SECS = 604_800;
